import React, { useMemo, useState } from "react";
import type { ProductItem } from "../services/api";
import ContentCard from "../components/ContentCard";
import "./CatalogPage.css";

interface CatalogPageProps {
  products: ProductItem[];
  getProductActionLabel: (product: ProductItem) => string;
  loadingData: boolean;
  pageError: string;
}

const isExternal = (href: string) => /^https?:\/\//i.test(href);

function productHref(product: ProductItem) {
  return product.accessUrl || product.productUrl || product.checkoutUrl || "#/catalogo";
}

function productBadge(product: ProductItem) {
  if (/vip|comunidade|grupo/i.test(product.title)) return "Comunidade";
  if (product.isOffer) return "Oferta";
  return product.badge || "Ferramenta";
}

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "unlocked", label: "Liberadas" },
  { key: "locked", label: "Bloqueadas" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

const CatalogPage: React.FC<CatalogPageProps> = ({
  products,
  getProductActionLabel,
  loadingData,
  pageError,
}) => {
  const [filter, setFilter] = useState<FilterKey>("all");

  const tools = useMemo(
    () =>
      products
        .filter((p) => !p.isCourse)
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [products],
  );

  const filtered = useMemo(() => {
    if (filter === "unlocked") return tools.filter((p) => p.hasAccess);
    if (filter === "locked") return tools.filter((p) => !p.hasAccess);
    return tools;
  }, [tools, filter]);

  const counts = useMemo(
    () => ({
      all: tools.length,
      unlocked: tools.filter((p) => p.hasAccess).length,
      locked: tools.filter((p) => !p.hasAccess).length,
    }),
    [tools],
  );

  return (
    <div className="bc-page">
      <header className="bc-page-head">
        <span className="bc-eyebrow"><i />Ferramentas</span>
        <h1>Ferramentas &amp; acessos</h1>
        <p>Desbloqueie ferramentas, grupos e acessos premium da Blue Capital.</p>
      </header>

      <div className="bc-tabs" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={`bc-tab ${filter === f.key ? "active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span className="bc-tab-count">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {pageError ? <p className="bc-empty">{pageError}</p> : null}

      <div className="bc-grid">
        {loadingData
          ? Array.from({ length: 6 }).map((_, i) => (
              <div className="cc cc-skeleton" key={i} aria-hidden="true">
                <div className="cc-thumb" />
                <div className="cc-body">
                  <span className="sk sk-1" />
                  <span className="sk sk-2" />
                  <span className="sk sk-3" />
                </div>
              </div>
            ))
          : filtered.length
            ? filtered.map((product, index) => {
                const href = productHref(product);
                return (
                  <ContentCard
                    key={product.id}
                    title={product.title}
                    description={product.description}
                    cover={product.banner}
                    badge={productBadge(product)}
                    locked={!product.hasAccess}
                    meta={product.hasAccess ? "Liberado" : "Bloqueado"}
                    actionLabel={getProductActionLabel(product)}
                    href={href}
                    external={isExternal(href)}
                    toneIndex={index}
                  />
                );
              })
            : <p className="bc-empty">Nenhuma ferramenta nesta categoria.</p>}
      </div>
    </div>
  );
};

export default CatalogPage;
